import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTheme } from '@mui/material/styles';

interface WebTimePickerProps {
  open: boolean;
  initialTime: Date;
  onConfirm: (time: Date) => void;
  onCancel: () => void;
}

const WebTimePicker: React.FC<WebTimePickerProps> = ({
  open,
  initialTime,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const [selectedTime, setSelectedTime] = useState<Date | null>(initialTime);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Update selectedTime when initialTime changes
  React.useEffect(() => {
    setSelectedTime(initialTime);
  }, [initialTime]);

  const handleConfirm = () => {
    console.log('WebTimePicker confirm:', selectedTime);
    if (selectedTime) {
      onConfirm(selectedTime);
    }
  };

  const roundToNearest15Minutes = (date: Date): Date => {
    const minutes = date.getMinutes();
    const roundedMinutes = Math.round(minutes / 15) * 15;
    const newDate = new Date(date);
    newDate.setMinutes(roundedMinutes);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    return newDate;
  };

  const handleTimeChange = (newTime: Date | null) => {
    console.log('WebTimePicker time change:', newTime);
    if (newTime && newTime instanceof Date && !isNaN(newTime.getTime())) {
      const roundedTime = roundToNearest15Minutes(newTime);
      console.log('Rounded time:', roundedTime);
      setSelectedTime(roundedTime);
    } else {
      console.log('Invalid time, setting to null');
      setSelectedTime(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: theme.custom.background1,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: theme.custom.text,
          borderBottom: `1px solid ${theme.custom.stroke}`,
        }}
      >
        Select Time
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <TimePicker
              value={selectedTime}
              onChange={handleTimeChange}
              minutesStep={15}
              ampm
              open={pickerOpen}
              onOpen={() => setPickerOpen(true)}
              onClose={() => setPickerOpen(false)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.custom.background2,
                      borderRadius: '8px',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: theme.custom.text,
                    },
                    '& .MuiInputAdornment-root': {
                      '& .MuiIconButton-root': {
                        color: theme.custom.text,
                      },
                    },
                  }}
                />
              )}
            />
          </Box>
        </LocalizationProvider>

        <Typography
          variant="body2"
          sx={{
            mt: 2,
            color: theme.custom.textLight,
            textAlign: 'center',
          }}
        >
          Times are automatically rounded to 15-minute intervals
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.custom.stroke}`,
          gap: 1,
        }}
      >
        <Button
          onClick={onCancel}
          sx={{
            color: theme.custom.textLight,
            '&:hover': {
              backgroundColor: theme.custom.fill1,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedTime}
          sx={{
            backgroundColor: theme.custom.primary,
            color: theme.custom.textCTA,
            '&:hover': {
              backgroundColor: theme.custom.primaryDark,
            },
            '&:disabled': {
              backgroundColor: theme.custom.fill2,
              color: theme.custom.textLight,
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebTimePicker;
